import { useCallback, useEffect, useState } from 'react';
import { X, ExternalLink } from 'lucide-react';
import {
  getCrmClient360,
  getCrmClientActivities,
  patchCrmClientTags,
  patchCrmClientDepartment,
  type CrmClient360,
} from '../../../api';
import { CRM_ACTIVITY_KIND_LABELS } from './crmActivityLabels';
import { canCrmEditClientTags, canCrmEditDepartment, type CrmAccessParams } from '../../../crmAccess';
import { isProductionCrmRead } from '../../../crmAccess';
import { openTelegramUserDm } from '../../../openTelegramUser';
import { CrmChatThread } from './CrmChatThread';
import { CrmClientContactEditor, type CrmClientContactValues } from './CrmClientContactEditor';
import { CrmDealAccordion } from './CrmDealAccordion';
import { SALES_STAGE_META } from './crmLabels';
import type { SalesStage } from '../../../api';
import { AdminClientDealsStrip } from '../AdminClientDealsStrip';
import { AdminOverlayPortal } from '../AdminOverlayPortal';

type Props = {
  userId: number;
  access: CrmAccessParams;
  onClose: () => void;
  hapticSelection: () => void;
  hapticSuccess?: () => void;
  onOpenDeal?: (orderId: number) => void;
};

export function CrmClient360Panel({
  userId,
  access,
  onClose,
  hapticSelection,
  hapticSuccess,
  onOpenDeal,
}: Props) {
  const canEditTags = canCrmEditClientTags(access);
  const canEditDept = canCrmEditDepartment(access);
  const canEditContact = !isProductionCrmRead(access);
  const [data, setData] = useState<CrmClient360 | null>(null);
  const [contact, setContact] = useState<CrmClientContactValues | null>(null);
  const [tagInput, setTagInput] = useState('');
  const [dept, setDept] = useState('');
  const [loading, setLoading] = useState(true);
  const [activityDays, setActivityDays] = useState<Record<string, Array<{ kind: string; body: string }>>>({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [res, acts] = await Promise.all([
        getCrmClient360(userId),
        getCrmClientActivities(userId).catch(() => ({ by_date: {} })),
      ]);
      setData(res);
      const cc = res.client_contact;
      setContact({
        name: cc?.name ?? res.user.first_name ?? null,
        phone: cc?.phone ?? res.user.phone ?? null,
        company: cc?.company ?? null,
        delivery_address: cc?.delivery_address ?? null,
      });
      setTagInput((res.user.crm_tags || []).join(', '));
      setDept(res.user.crm_department || '');
      setActivityDays(acts.by_date || {});
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    void load();
  }, [load]);

  const saveTags = async () => {
    if (!canEditTags) return;
    const tags = tagInput.split(',').map((t) => t.trim()).filter(Boolean);
    await patchCrmClientTags(userId, tags);
    hapticSelection();
    await load();
  };

  const saveDept = async () => {
    if (!canEditDept) return;
    await patchCrmClientDepartment(userId, dept || null);
    hapticSelection();
    await load();
  };

  if (loading && !data) {
    return (
      <AdminOverlayPortal zIndex={85}>
        <div className="flex flex-1 items-center justify-center min-h-0">
          <p className="text-sm" style={{ color: 'var(--text-3)' }}>
            Загрузка…
          </p>
        </div>
      </AdminOverlayPortal>
    );
  }

  if (!data || !contact) return null;
  const u = data.user;
  const displayName = contact.name || u.first_name || u.username || `Клиент #${u.id}`;

  return (
    <AdminOverlayPortal zIndex={85}>
    <div className="flex flex-col flex-1 min-h-0 w-full">
      <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: 'var(--border-subtle)' }}>
        <div>
          <p className="text-sm font-bold" style={{ color: 'var(--text-1)' }}>
            {displayName}
          </p>
          <p className="text-xs" style={{ color: 'var(--text-3)' }}>
            Клиент 360° · {u.role}
            {u.username ? ` · @${u.username}` : ''}
          </p>
        </div>
        <div className="flex gap-2">
          {u.telegram_id && (
            <button
              type="button"
              className="press p-2 rounded-full"
              style={{ background: 'rgba(99,179,237,0.15)' }}
              onClick={() => openTelegramUserDm(u.telegram_id, u.username)}
            >
              <ExternalLink size={18} color="#63B3ED" />
            </button>
          )}
          <button type="button" onClick={onClose} className="press p-2 rounded-full" style={{ background: 'rgba(255,255,255,0.08)' }}>
            <X size={20} color="var(--text-2)" />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2">
        {onOpenDeal && (
          <AdminClientDealsStrip userId={userId} hapticSelection={hapticSelection} onOpenDeal={onOpenDeal} />
        )}

        <CrmDealAccordion
          title="Контакты"
          subtitle={[contact.phone, contact.delivery_address].filter(Boolean).join(' · ').slice(0, 56)}
          accent="#68D391"
          defaultOpen={false}
        >
          <CrmClientContactEditor
            initial={contact}
            userId={userId}
            syncOrderId={data.primary_deal_id}
            canEdit={canEditContact}
            hapticSelection={hapticSelection}
            hapticSuccess={hapticSuccess}
            onSaved={(next) => {
              setContact(next);
              void load();
            }}
          />
        </CrmDealAccordion>

        {(canEditTags || canEditDept) && (
          <CrmDealAccordion
            title="Метки CRM"
            subtitle="Для фильтров и отчётов"
            accent="#B794F4"
            defaultOpen={false}
          >
            <p className="text-[10px] leading-snug mb-3" style={{ color: 'var(--text-3)' }}>
              Отдел — в какой колонке/воронке виден клиент у владельца платформы. Теги — произвольные метки
              (vip, повторный) для поиска и сегментации; на этапы заказа не влияют.
            </p>
            {canEditDept && (
              <>
                <p className="text-[10px] font-bold uppercase mb-1" style={{ color: 'var(--text-3)' }}>
                  Отдел CRM
                </p>
                <select className="input-glass w-full text-sm mb-2" value={dept} onChange={(e) => setDept(e.target.value)}>
                  <option value="">— не задан —</option>
                  <option value="sales">Продажи</option>
                  <option value="production">Производство</option>
                  <option value="measurement">Замеры</option>
                </select>
                <button
                  type="button"
                  onClick={() => void saveDept()}
                  className="press text-xs font-bold px-3 py-1.5 rounded-lg mb-3 touch-manipulation min-h-[40px]"
                  style={{ background: 'rgba(159,122,234,0.15)', color: '#B794F4' }}
                >
                  Сохранить отдел
                </button>
              </>
            )}
            {canEditTags && (
              <>
                <p className="text-[10px] font-bold uppercase mb-1" style={{ color: 'var(--text-3)' }}>
                  Теги
                </p>
                <input
                  className="input-glass w-full text-sm mb-2"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  placeholder="vip, повторный"
                />
                <button
                  type="button"
                  onClick={() => void saveTags()}
                  className="press text-xs font-bold px-3 py-1.5 rounded-lg touch-manipulation min-h-[40px]"
                  style={{ background: 'rgba(99,179,237,0.15)', color: '#63B3ED' }}
                >
                  Сохранить теги
                </button>
              </>
            )}
          </CrmDealAccordion>
        )}

        {Object.keys(activityDays).length > 0 && (
          <CrmDealAccordion title="История контактов" subtitle="Звонки и заметки" accent="#FC814A" defaultOpen={false}>
            {Object.entries(activityDays).slice(0, 4).map(([day, items]) => (
              <div key={day} className="mb-2">
                <p className="text-[10px] mb-1" style={{ color: 'var(--text-3)' }}>
                  {day}
                </p>
                {items.slice(0, 3).map((a, i) => (
                  <p
                    key={i}
                    className="text-xs p-2 rounded-lg mb-1"
                    style={{ background: 'rgba(255,255,255,0.04)', color: 'var(--text-2)' }}
                  >
                    {CRM_ACTIVITY_KIND_LABELS[a.kind] || a.kind}: {a.body}
                  </p>
                ))}
              </div>
            ))}
          </CrmDealAccordion>
        )}

        <CrmDealAccordion
          title={`Заказы (${data.orders.length})`}
          subtitle="Все сделки ONV"
          accent="#63B3ED"
          defaultOpen={false}
        >
          <div className="space-y-2">
            {data.orders.map((o) => {
              const st = (o.sales_stage || 'lead_received') as SalesStage;
              const meta = SALES_STAGE_META[st] || SALES_STAGE_META.lead_received;
              return (
                <button
                  key={o.id}
                  type="button"
                  className="press w-full text-left p-3 rounded-xl touch-manipulation"
                  style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid var(--border-subtle)' }}
                  onClick={() => {
                    hapticSelection();
                    onOpenDeal?.(o.id);
                  }}
                >
                  <div className="flex justify-between">
                    <span className="text-xs font-bold" style={{ color: 'var(--text-1)' }}>
                      {o.order_number}
                    </span>
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded" style={{ background: meta.bg, color: meta.color }}>
                      {meta.label}
                    </span>
                  </div>
                  <p className="text-sm font-bold mt-1" style={{ color: 'var(--cyan-bright)' }}>
                    {Math.round(o.final_amount || 0).toLocaleString('ru-RU')} ₽
                  </p>
                </button>
              );
            })}
          </div>
        </CrmDealAccordion>

        {data.measurements.length > 0 && (
          <CrmDealAccordion title="Замеры" subtitle={`${data.measurements.length} заявок`} accent="#F6AD55" defaultOpen={false}>
            {data.measurements.map((m) => (
              <p key={m.id} className="text-xs mb-1" style={{ color: 'var(--text-2)' }}>
                #{m.id} · {m.status} · {m.city || '—'}
              </p>
            ))}
          </CrmDealAccordion>
        )}

        {u.telegram_id && (
          <CrmDealAccordion title="Переписка" subtitle="Telegram" accent="#D6BCFA" defaultOpen={false}>
            <CrmChatThread userId={u.id} hapticSelection={hapticSelection} />
          </CrmDealAccordion>
        )}
      </div>
    </div>
    </AdminOverlayPortal>
  );
}
